import React, { useEffect, useMemo, useState } from 'react'
import { Tabs, Skeleton } from 'antd'
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import ChangePasswordTab from './components/changePassword';
import './myProfile.css'
import MyPersonalDetails from './components/personalDetails';
import UsageCard from './components/UsageCard';
import CurrentPlanCard from './components/CurrentPlanCard';
import BuyCreditsModal from '../../components/BuyCreditsModal/BuyCreditsModal';
import ManageSubscriptionModal from '../../components/ManageSubscriptionModal/ManageSubscriptionModal';
import CreditsBanner from '../../components/CreditsBanner/CreditsBanner';
import history from '../../routes/history';
import {
    fetchCreditsBalanceRequested,
    fetchPlansRequested,
} from '../../redux/actions';
import {
    getSelectedPlanFromPlanCode,
    getPlanDetailsForSelectedPlan,
    normalizeSelectedPlan,
    canUseSubscriptionFeatures,
    openManageSubscriptionModal,
    openUpgradePlanModal,
} from '../../shared/billing';
import {
    CREDIT_ALERT_TYPE_PAYMENT_FAILED,
    CREDIT_ALERT_TYPE_PILOT_EXPIRED,
    CREDIT_ALERT_TYPE_SUBSCRIPTION_EXPIRED,
    CREDIT_ALERT_TYPE_SUBSCRIPTION_EXPIRING_SOON,
    CREDIT_ALERT_TYPE_TRIAL_ENDING_SOON,
} from '../../shared/creditsBanner';

const getCreditsBalancePayload = (creditsBalance) => {
    if (creditsBalance?.data && typeof creditsBalance.data === 'object') {
        return creditsBalance.data;
    }

    return creditsBalance || {};
};

const getCreditsBannerPayload = (banner) => {
    if (banner?.data && typeof banner.data === 'object') {
        if ('banner' in banner.data) {
            return banner.data.banner;
        }

        return banner.data;
    }

    if (banner && typeof banner === 'object' && 'banner' in banner) {
        return banner.banner;
    }

    return banner || {};
};

const getSelectedPlanKeyFromBalance = (creditsBalance) => {
    const balancePayload = getCreditsBalancePayload(creditsBalance);
    const normalizedPlanCode = normalizeSelectedPlan(
        balancePayload?.planCode ||
        balancePayload?.productCode ||
        ''
    );

    if (normalizedPlanCode.includes('starter')) {
        return 'starter';
    }

    return '';
};

const formatPlanNameFromCode = (planCode = '') => {
    const normalizedPlanCode = normalizeSelectedPlan(planCode);

    if (normalizedPlanCode.includes('starter')) {
        return 'Starter';
    }

    if (!normalizedPlanCode) {
        return 'Free';
    }

    return normalizedPlanCode
        .split('_')
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const getPlanBillingSubtitle = (planCode = '') => {
    const normalizedPlanCode = normalizeSelectedPlan(planCode);

    if (normalizedPlanCode.includes('yearly') || normalizedPlanCode.includes('annual')) {
        return 'Per year';
    }

    return 'Per month';
};

const getAvailableCredits = (creditsBalance) => {
    const balancePayload = getCreditsBalancePayload(creditsBalance);

    return Number(balancePayload?.totalCreditsBalance) || 0;
};

const getUsageMetrics = (creditsBalance) => {
    const balancePayload = getCreditsBalancePayload(creditsBalance);
    const remainingCredits = Number(balancePayload?.totalCreditsBalance) || 0;
    const grantedPlanCredits = Number(balancePayload?.creditsGranted) || 0;
    const topupCredits = Number(balancePayload?.topupCreditsBalance) || 0;
    const totalCredits = Math.max(
        remainingCredits,
        grantedPlanCredits + topupCredits
    );

    return {
        remainingCredits,
        totalCredits,
    };
};

const formatUsdPrice = (value) => {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue) || numericValue <= 0) {
        return '$0';
    }

    return `$${Number(numericValue.toFixed(2)).toString()}`;
};

const shouldOpenUpgradePlanModalFromBanner = (payload = {}) =>
    [
        CREDIT_ALERT_TYPE_PAYMENT_FAILED,
        CREDIT_ALERT_TYPE_PILOT_EXPIRED,
        CREDIT_ALERT_TYPE_SUBSCRIPTION_EXPIRED,
    ].includes(String(payload?.type || '').toUpperCase());

const shouldOpenManageSubscriptionModalFromBanner = (payload = {}) =>
    [
        CREDIT_ALERT_TYPE_SUBSCRIPTION_EXPIRING_SOON,
        CREDIT_ALERT_TYPE_TRIAL_ENDING_SOON,
    ].includes(String(payload?.type || '').toUpperCase());

const shouldOpenSubscriptionModalFromBannerButton = (payload = {}) =>
    String(payload?.buttonText || '').toLowerCase() === 'upgrade plan';

const shouldOpenBuyCreditsModalFromBanner = (payload = {}) =>
    ['buy credits', 'buy more'].includes(
        String(payload?.buttonText || '').toLowerCase()
    );

const isPlanExpired = (creditsBalance) => {
    const balancePayload = getCreditsBalancePayload(creditsBalance);
    const normalizedStatus = normalizeSelectedPlan(balancePayload?.status);

    if (balancePayload?.hasActivePlan === false) {
        return true;
    }

    if (['expired', 'cancelled', 'canceled',].includes(normalizedStatus)) {
        return true;
    }

    return false;
};

const ProfileBillingCard = ({ className, loading, children }) => {
    if (loading) {
        return (
            <div className={className}>
                <Skeleton active paragraph={{ rows: 3 }} />
            </div>
        );
    }

    return children;
};

const MyProfile = () => {
    const { TabPane } = Tabs;
    const dispatch = useDispatch();
    const userProfile = JSON.parse(localStorage.getItem("userProfile"));
    const isMasterAccount = userProfile?.MasterAccountId === null || userProfile?.MasterAccountId === undefined;
    const subscriptionFeaturesEnabled = canUseSubscriptionFeatures(userProfile);
    const [creditsBanner, setCreditsBanner] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('creditsBanner'));
        } catch (error) {
            return null;
        }
    });
    const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);
    const [showManageSubscriptionModal, setShowManageSubscriptionModal] = useState(false);
    const [manageSubscriptionLoading] = useState(false);
    const {
        loading: plansLoading,
        plans,
        plansDirty,
        creditsBalance,
        creditsBalanceLoading,
        creditsBalanceLoaded,
    } = useSelector((state) => state.plansReducer, shallowEqual);

    useEffect(() => {
        if (!isMasterAccount || !subscriptionFeaturesEnabled) {
            return;
        }

        if (!creditsBalanceLoaded && !creditsBalanceLoading) {
            dispatch(fetchCreditsBalanceRequested());
        }

        if (plansDirty && !plansLoading) {
            dispatch(fetchPlansRequested());
        }
    }, [
        creditsBalanceLoaded,
        creditsBalanceLoading,
        dispatch,
        isMasterAccount,
        plansDirty,
        plansLoading,
        subscriptionFeaturesEnabled,
    ]);

    useEffect(() => {
        if (!subscriptionFeaturesEnabled) {
            setCreditsBanner(null);
            return undefined;
        }

        const syncCreditsBanner = (event) => {
            if (event?.detail !== undefined) {
                setCreditsBanner(event.detail);
                return;
            }

            try {
                setCreditsBanner(JSON.parse(localStorage.getItem('creditsBanner')));
            } catch (error) {
                setCreditsBanner(null);
            }
        };

        window.addEventListener('credits-banner-updated', syncCreditsBanner);

        return () => {
            window.removeEventListener('credits-banner-updated', syncCreditsBanner);
        };
    }, [subscriptionFeaturesEnabled]);

    const currentPlanCardProps = useMemo(() => {
        const balancePayload = getCreditsBalancePayload(creditsBalance);
        const selectedPlanKey = getSelectedPlanKeyFromBalance(creditsBalance);
        const planDetails = getPlanDetailsForSelectedPlan(plans, selectedPlanKey);
        const hasActivePlan =
            balancePayload?.hasActivePlan === true ||
            normalizeSelectedPlan(balancePayload?.status) === 'active' ||
            Boolean(balancePayload?.planCode || balancePayload?.currentPeriodStart);
        const creditsGranted = Number(balancePayload?.creditsGranted) || 0;

        if (!hasActivePlan) {
            return {
                planName: 'Current Plan',
                tier: 'Free',
                price: '$0',
                subtitle: 'Per month',
                usageLabel: 'No active paid plan',
                percentage: 100,
            };
        }

        const planName =
            planDetails?.name ||
            formatPlanNameFromCode(balancePayload?.planCode);
        const monthlyPrice =
            Number(balancePayload?.planPriceUsd) ||
            Number(planDetails?.monthlyPrice) ||
            0;

        return {
            planName: 'Current Plan',
            tier: planName,
            price: formatUsdPrice(monthlyPrice),
            subtitle: getPlanBillingSubtitle(balancePayload?.planCode),
            usageLabel: creditsGranted > 0
                ? `${creditsGranted} monthly credits included`
                : `Status: ${balancePayload?.status || 'active'}`,
            percentage: 100,
        };
    }, [creditsBalance, plans]);
    const usageMetrics = useMemo(
        () => getUsageMetrics(creditsBalance),
        [creditsBalance]
    );
    const planExpired = useMemo(
        () => isPlanExpired(creditsBalance),
        [creditsBalance]
    );
    const topUpDisabledTooltip = 'Your plan has expired. Activate a plan to add top-ups.';
    const billingCardsLoading = creditsBalanceLoading && !creditsBalanceLoaded;
    return (
        <div className='myProfile-main-div'>
            {subscriptionFeaturesEnabled && <BuyCreditsModal
                visible={showBuyCreditsModal}
                onClose={() => setShowBuyCreditsModal(false)}
                onUpgradeToPlan={() => setShowManageSubscriptionModal(true)}
                availableCredits={getAvailableCredits(creditsBalance)}
            />}
            {subscriptionFeaturesEnabled && <ManageSubscriptionModal
                visible={showManageSubscriptionModal}
                onClose={() => setShowManageSubscriptionModal(false)}
                plans={plans}
                creditsBalance={creditsBalance}
                loading={plansLoading}
            />}
            {subscriptionFeaturesEnabled && <div className='myProfile-banner-shell'>
                <CreditsBanner
                    banner={creditsBanner}
                    hideAction={!isMasterAccount}
                    actionDisabled={planExpired && shouldOpenBuyCreditsModalFromBanner(getCreditsBannerPayload(creditsBanner))}
                    actionDisabledTooltip={topUpDisabledTooltip}
                    onAction={(payload = {}) => {
                        if (shouldOpenBuyCreditsModalFromBanner(payload) && !planExpired) {
                            setShowBuyCreditsModal(true);
                            return;
                        }

                        if (shouldOpenSubscriptionModalFromBannerButton(payload)) {
                            setShowManageSubscriptionModal(true);
                            return;
                        }

                        if (shouldOpenManageSubscriptionModalFromBanner(payload)) {
                            openManageSubscriptionModal({
                                selectedPlan: getSelectedPlanFromPlanCode(
                                    payload?.planCode || ''
                                ),
                                allowCurrentPlanCheckout: true,
                            });
                            return;
                        }

                        if (shouldOpenUpgradePlanModalFromBanner(payload)) {
                            openUpgradePlanModal(
                                getSelectedPlanFromPlanCode(
                                    payload?.planCode || ''
                                )
                            );
                            return;
                        }

                        setShowManageSubscriptionModal(true);
                    }}
                />
            </div>}

            {subscriptionFeaturesEnabled && isMasterAccount && (
                <div className='myProfile-top-shell'>
                    <div className='myProfile-cards-section'>
                        <div className='myProfile-cards-grid'>
                            <ProfileBillingCard className='myProfile-plan-card' loading={billingCardsLoading}>
                                <CurrentPlanCard
                                    {...currentPlanCardProps}
                                    onManageSubscription={() => setShowManageSubscriptionModal(true)}
                                    manageSubscriptionLoading={manageSubscriptionLoading}
                                />
                            </ProfileBillingCard>
                            <ProfileBillingCard className='myProfile-credit-card' loading={billingCardsLoading}>
                                <UsageCard
                                    remainingCredits={usageMetrics.remainingCredits}
                                    totalCredits={usageMetrics.totalCredits}
                                    onHistoryClick={() => history.push('/transaction-history')}
                                    onBuyMoreClick={() => setShowBuyCreditsModal(true)}
                                    buyMoreDisabled={planExpired}
                                    buyMoreTooltip={topUpDisabledTooltip}
                                />
                            </ProfileBillingCard>
                        </div>
                    </div>
                </div>
            )}

            <div className='myProfile-body'>
                <div className='myProfile-heading'>
                    My profile
                </div>
                <div className='myProfile-tabs-wrap'>
                    <Tabs 
                    
                    defaultActiveKey="1" 
                    tabBarGutter={75}  
                    tabBarStyle={{
                        paddingLeft:'3%',
                        color: '#7e7e7e',
                    }}
                    >
                        <TabPane 
                        tab="Personal details" 
                        key="1" 
                        >
                            <MyPersonalDetails />
                        </TabPane>
                        <TabPane tab="Change password" key="2">
                            <ChangePasswordTab />
                        </TabPane>
                    </Tabs>
                </div>
            </div>
        </div>
    )
}

export default MyProfile;
